/**
 * DiffSection - Side-by-side diff visualization for v2 AI action cards.
 *
 * Two mutually exclusive modes:
 * - `stageTransition`: delegates to StageTransitionPreview for pipeline stage changes
 * - `rows`: generic current/proposed value pairs rendered as a labeled grid
 *
 * Rows support read-only display, click-to-edit, and inline editing:
 * - `onEditProposed` turns the proposed cell into a click target that hands
 *   editing back to the caller (which then re-renders the row with an input).
 * - `isEditable: true` means `proposedValue` IS an interactive input, so the
 *   decorative proposed-value wrapper is skipped and the input renders directly
 *   in the grid cell.
 *
 * Long values: a row marked `isExpandable` owns ONE expand/collapse toggle that
 * drives BOTH sides, so current and proposed never disagree about how much text
 * is showing. The state is published via `DiffRowExpansionProvider`; value
 * renderers read it with `useDiffRowExpanded()` and must not draw their own
 * toggle (see `PropertyValueLabel`'s `hideExpandButton`).
 *
 * If neither prop is provided, renders nothing.
 */

import { ArrowRight, ChevronDown, ChevronUp } from '@untitledui/icons';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import {
  StageTransitionPreview,
  type StageTransitionPreviewProps,
} from '@/components/proposals/artifacts/StageTransitionPreview';
import { Button } from '@/lib/base/buttons/button';
import { TruncatedNode } from '@/lib/base/truncated-text';
import { cx } from '@/utils/cx';

import { DiffRowExpansionProvider } from './diffRowExpansion';
import { ArrowConnector } from './shared/ArrowConnector';

export interface DiffRow {
  /** Unique identifier for stable React reconciliation. Falls back to `label` if omitted. */
  id?: string;
  label: string;
  currentValue: ReactNode;
  proposedValue: ReactNode;
  /**
   * When true, `proposedValue` is an interactive input and renders directly
   * in the grid cell without the decorative success-colored wrapper.
   */
  isEditable?: boolean;
  /**
   * Values wrap across lines instead of hard-truncating to one. Use for rows
   * whose renderer already handles its own length capping.
   */
  multiline?: boolean;
  /**
   * Row owns a single expand/collapse toggle driving BOTH sides. Implies
   * `multiline`. Only set this when a side is actually long enough to
   * truncate, otherwise the toggle is a button that does nothing.
   */
  isExpandable?: boolean;
  /**
   * Makes the proposed cell a click target that starts editing this row.
   * Ignored while `isEditable` is true (the input already has focus).
   */
  onEditProposed?: () => void;
  /** Accessible name for the click-to-edit target. Required with `onEditProposed`. */
  editLabel?: string;
}

export interface DiffSectionStageTransition {
  currentValue: string | null | undefined;
  proposedValue: string | null | undefined;
  /** CRM entity type — passed straight through to StageTransitionPreview */
  entityType: StageTransitionPreviewProps['entityType'];
}

export interface DiffSectionProps {
  /**
   * Generic property diff rows.
   * Mutually exclusive with `stageTransition`.
   */
  rows?: DiffRow[];
  /** Column header for the left (current) side. Defaults to "Current". */
  currentLabel?: string;
  /** Column header for the right (proposed) side. Defaults to "Updated". */
  proposedLabel?: string;
  /**
   * Stage transition data — renders StageTransitionPreview instead of the generic grid.
   * Mutually exclusive with `rows`.
   */
  stageTransition?: DiffSectionStageTransition;
  className?: string;
}

/**
 * Shared chrome for the read-only "current" cell — deliberately faded: it is
 * the value being left behind, so it recedes next to the proposed one.
 */
const CURRENT_CELL_CLASS =
  'rounded-md border border-border-secondary bg-bg-secondary px-2.5 py-1.5 text-sm text-fg-tertiary min-w-0';

/**
 * Shared chrome for the read-only "proposed" cell — brand-tinted and a
 * weight up, so the eye lands on what approval will write.
 */
const PROPOSED_CELL_CLASS =
  'rounded-md border border-utility-brand-200 bg-utility-brand-50 px-2.5 py-1.5 text-sm font-medium text-utility-brand-700 min-w-0';

/**
 * DiffSection - Visualizes property changes for a v2 action card.
 *
 * Pass `stageTransition` for pipeline stage changes (uses StageTransitionPreview).
 * Pass `rows` for generic before/after property changes.
 * If neither is provided, the component renders null.
 */
export function DiffSection({
  rows,
  currentLabel = 'Current',
  proposedLabel = 'Updated',
  stageTransition,
  className,
}: DiffSectionProps) {
  // Stage transition mode — delegate to StageTransitionPreview
  if (stageTransition) {
    return (
      <div className={cx('px-3 py-2.5', className)}>
        <StageTransitionPreview
          currentValue={stageTransition.currentValue}
          proposedValue={stageTransition.proposedValue}
          entityType={stageTransition.entityType}
        />
      </div>
    );
  }

  // Generic rows mode
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <div className={cx('space-y-2', className)}>
      {/* Column headers */}
      <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-1'>
        <span className='text-xs font-medium text-fg-tertiary uppercase tracking-wide'>
          {currentLabel}
        </span>
        <span className='w-5' aria-hidden='true' />
        <span className='text-xs font-medium text-fg-tertiary uppercase tracking-wide'>
          {proposedLabel}
        </span>
      </div>

      {/* Diff rows */}
      <div className='space-y-1.5'>
        {rows.map(row => (
          <DiffRowItem key={row.id ?? row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

interface DiffRowProps {
  row: DiffRow;
}

function DiffRowItem({ row }: DiffRowProps) {
  // Row-owned expansion: one toggle, both sides. Kept here (not in the value
  // renderers) so the two columns can never show different amounts of text.
  const [isExpanded, setIsExpanded] = useState(false);
  const handleToggleExpanded = useCallback(
    () => setIsExpanded(prev => !prev),
    []
  );

  // A row that is expandable owns its own "Show more" toggle, so it must be
  // free to wrap — clipping it would hide text the toggle promises to reveal.
  // Everything else is capped to one line: without a cap, a value with no
  // break opportunity (an email, a URL, a long token) renders at its
  // min-content width and escapes the cell's border, colliding with the arrow
  // and the neighbouring column. `break-words` alone does not clip — it only
  // offers a break opportunity the renderer may decline.
  const wraps = row.isExpandable;
  const valueClass = cx('min-w-0 overflow-hidden', wraps && 'break-words');
  const { onEditProposed } = row;
  // Non-expandable rows clip to one line, so the full value needs somewhere to
  // go: `TruncatedNode` reveals it in a tooltip, and only while it is actually
  // clipped. Expandable rows keep their raw node — their toggle is the reveal.
  const currentValue = wraps ? (
    row.currentValue
  ) : (
    <TruncatedNode>{row.currentValue}</TruncatedNode>
  );
  // Never wrap an editable cell: its proposed value is a live input, and
  // clipping it to one line (or hanging a tooltip off it) would fight the
  // control the user is typing into.
  const proposedValue =
    wraps || row.isEditable ? (
      row.proposedValue
    ) : (
      <TruncatedNode>{row.proposedValue}</TruncatedNode>
    );
  const canStartEdit = !row.isEditable && !!onEditProposed;
  const handleEditKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        onEditProposed?.();
      } else if (event.key === ' ') {
        event.preventDefault();
        onEditProposed?.();
      }
    },
    [onEditProposed]
  );

  return (
    <DiffRowExpansionProvider value={isExpanded}>
      {/* data-row-id per the E2E handle contract: lets a test (or a Playwright
          spec) scope assertions to ONE property's current/proposed pair rather
          than searching the whole card, which is how a value landing in the
          wrong column goes unnoticed. */}
      <div className='space-y-1' data-row-id={row.id ?? row.label}>
        {/* Row label */}
        <div className='text-xs text-fg-tertiary px-1'>{row.label}</div>

        {/* Current → Proposed */}
        <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2'>
          {/* Current value — always read-only: it is existing CRM state. */}
          <div className={CURRENT_CELL_CLASS} data-diff-side='current'>
            <div className={valueClass}>{currentValue}</div>
          </div>

          {/* Arrow */}
          <ArrowConnector size='sm'>
            <ArrowRight className='w-3 h-3' />
          </ArrowConnector>

          {/* Proposed value — editable rows render the input directly,
              read-only rows get the success wrapper (as a click target when
              the caller supports editing this row). */}
          {row.isEditable && (
            <div className='min-w-0' data-diff-side='proposed'>
              {proposedValue}
            </div>
          )}
          {!row.isEditable && canStartEdit && (
            // A cell surface, not a design-system Button: it must be
            // indistinguishable from the read-only cell beside it, exactly
            // like the CRM table's editable cell. `role='button'` +
            // `tabIndex`/`onKeyDown` on a plain div (no native button
            // element) so it stays keyboard-reachable and announced as
            // activatable without picking up button chrome.
            <div
              role='button'
              tabIndex={0}
              data-diff-side='proposed'
              onClick={onEditProposed}
              onKeyDown={handleEditKeyDown}
              aria-label={row.editLabel}
              className={cx(
                PROPOSED_CELL_CLASS,
                'w-full text-left cursor-pointer transition-colors',
                'hover:border-utility-brand-300 hover:bg-utility-brand-100',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
              )}
            >
              <div className={valueClass}>{proposedValue}</div>
            </div>
          )}
          {!row.isEditable && !canStartEdit && (
            <div className={PROPOSED_CELL_CLASS} data-diff-side='proposed'>
              <div className={valueClass}>{proposedValue}</div>
            </div>
          )}
        </div>

        {row.isExpandable && (
          <div className='px-1'>
            <Button
              size='sm'
              color='link-color'
              onClick={handleToggleExpanded}
              aria-expanded={isExpanded}
              iconTrailing={isExpanded ? ChevronUp : ChevronDown}
              className='text-xs'
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          </div>
        )}
      </div>
    </DiffRowExpansionProvider>
  );
}
